<template>
  <section class="grid-cols-[212px_1fr] lg:grid flex-auto">
    <Html class="faq-page" />
    <nav class="hidden lg:block bg-black text-white py-20">
      <ul
        class="container space-y-4 sticky top-[calc(var(--header-height,0px)+32px)]"
      >
        <li
          v-for="(block, index) in data.list"
          :key="`${index}-${block.title}`"
        >
          <a
            :href="`#${useCamelCase(block.title)}-${index}`"
            class="hover:underline cursor-pointer w-fit"
            @click.prevent="
              scrollToAnchor(`${useCamelCase(block.title)}-${index}`)
            "
          >
            {{ block.title }}
          </a>
        </li>
      </ul>
    </nav>

    <div class="py-20 lg:px-14d 2xl:px-20 container">
      <h2
        v-if="data?.title"
        class="pb-12 col-span-2 ui-h1"
        v-html="data?.title"
      />
      <UiNativeSelect
        v-model="selectedItem"
        class="inline-block py-3 mb-8 text-xs lg:hidden"
        :name="$t('jumpToTopic')"
        :items="selectItems"
        title-key="name"
        :placeholder="$t('jumpToTopic')"
        select-class="border-none uppercase -ml-3"
      />

      <div
        class="flex flex-col justify-center lg:max-w-[720px] 3xl:max-w-[1054px]"
      >
        <ul class="space-y-12 w-full text-sm">
          <li v-for="(block, index) in listWithUniqueIds" :key="block.title">
            <div
              :id="`${useCamelCase(block.title)}-${index}`"
              class="uppercase font-medium pb-4"
            >
              {{ block.title }}
            </div>
            <ul>
              <li
                v-for="item in block.list"
                :key="item.title"
                class="border-t border-black last:border-b"
              >
                <div
                  class="flex cursor-pointer select-none items-center justify-between gap-4 py-4"
                  @click="toggleActive(item.id)"
                >
                  <div>{{ item.title }}</div>
                  <div>
                    <UiIcon
                      class="size-2.5 text-cerulean"
                      :name="activeItem !== item.id ? 'plus' : 'minus'"
                    />
                  </div>
                </div>

                <SharedCollapseTransition>
                  <div v-show="activeItem === item.id && item.text">
                    <div class="pb-4" v-html="item.text" />
                  </div>
                </SharedCollapseTransition>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { AccordionBlockData } from "ecom-types";

const props = defineProps<{ data: AccordionBlockData }>();

const activeItem = ref(-1);
const selectedItem = ref();

const listWithUniqueIds = computed(() => {
  let idCounter = 0;

  return props.data?.list?.map((section) => ({
    ...section,
    list: section.list.map((item) => ({
      ...item,
      id: idCounter++,
    })),
  }));
});

const selectItems = computed(
  () =>
    props.data?.list?.map((item, index) => ({
      id: `${useCamelCase(item.title)}-${index}`,
      name: item.title,
      value: item.title,
    })) || [],
);

watch(
  () => selectedItem.value,
  (newValue) => {
    if (newValue) {
      const matchedItem = selectItems.value.find(
        (item) => item.value === newValue,
      );
      scrollToAnchor(matchedItem?.id || "");
    }
  },
);

function scrollToAnchor(id: string) {
  const element = document.getElementById(id);
  if (element) {
    const styles = getComputedStyle(document.documentElement);
    const headerHeight =
      parseInt(styles.getPropertyValue("--header-top-height")) +
        parseInt(
          styles.getPropertyValue("--discount-compaign-visible-height"),
        ) || 0;

    const offsetPosition = element.offsetTop - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
}

function toggleActive(idx: number) {
  if (idx === activeItem.value) {
    activeItem.value = -1;
  } else {
    activeItem.value = idx;
  }
}
</script>

<style>
.faq-page #main-content .page-content,
.faq-page #main-content .blocks-container {
  @apply flex flex-col flex-auto;
}
</style>
